import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useRef, useState } from 'react';
import '../global.css';
import { AuthFetch, useAuth } from '../hooks/useAuth';
import { NO_MATCH_FIELD, RequestHistoryDomain } from '../lib/request-history';
import {
	formatTotal,
	pageLoadFailureMessage,
	SEARCH_TIMEOUT_MS,
	SEARCH_TOO_BROAD_MESSAGE,
	searchFailureMessage,
	SLOW_SEARCH_MESSAGE,
	SLOW_SEARCH_MS,
} from '../lib/search-results';
import {
	EnrichedOverridesSearchResponse,
	EnrichedTranslationsModel,
} from '../types';
import { Nav } from './Nav';
import { TranslationLine } from './TranslationLine';
import { UsedInAreaFilters } from './UsedInAreaFilters';

const isValidDateString = (value: unknown): value is string =>
	typeof value === 'string' && !Number.isNaN(Date.parse(value));

const assertValidUpdatedAtStrings = (
	response: EnrichedOverridesSearchResponse
) => {
	if (!Array.isArray(response.items)) {
		throw new Error('Invalid response shape: items must be an array');
	}

	response.items.forEach((item) => {
		if (!isValidDateString(item.updatedAt)) {
			throw new Error(
				`Invalid updatedAt date string for translationId ${item.translationId}`
			);
		}
	});
};

export const Overrides = () => {
	const [search, setSearch] = useState('');
	const [searchLocale, setSearchLocale] = useState('en-GB');
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(20);
	const [requiresManualTranslationsOnly, setRequiresManualTranslationsOnly] =
		useState(false);
	const [requestHistory, setRequestHistory] =
		useState<RequestHistoryDomain | ''>('');
	const [translationType, setTranslationType] = useState('');

	const [searchResults, setSearchResults] = useState<EnrichedTranslationsModel[]>([]);
	const [total, setTotal] = useState(0);
	const [totalPages, setTotalPages] = useState(0);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [noResultsMessage, setNoResultsMessage] = useState<string | null>(null);
	const [limitReached, setLimitReached] = useState(false);
	// the page whose results are on screen, 0 when none; lags `page` while a page loads or after it fails
	const [shownPage, setShownPage] = useState(0);
	// the pagination buttons sit below the results, so bring the user back up once the page settles
	const scrollToTopOnSettle = useRef(false);
	const [scrollRequest, setScrollRequest] = useState(0);
	const [slowSearch, setSlowSearch] = useState(false);

	const { authFetch } = useAuth({ scopes: [] });
	const queryClient = useQueryClient();

	const { data, refetch, isError, error, isFetching } =
		useQuery<EnrichedOverridesSearchResponse>({
		queryKey: ['translations'],
		queryFn: async ({ signal }) => {
			return fetchTranslations({
				search,
				locale: searchLocale,
				page,
				pageSize,
				requiresManualTranslationsOnly,
				requestHistory: requestHistory || null,
				translationType: translationType || null,
				authFetch,
				signal,
			});
		},
		retry: false,
		enabled: false,
		// don't keep results around after unmount, otherwise navigating back to
		// this tab renders the previous search's cached results
		gcTime: 0,
		// always hand back a new data reference so results cleared by a new
		// search are repopulated even when the response is unchanged
		structuralSharing: false,
		});


	useEffect(() => {
		// clean up SSO redirect
		window.location.hash = '';
	}, []);

	useEffect(() => {
		if (scrollToTopOnSettle.current && (data || isError)) {
			scrollToTopOnSettle.current = false;
			setScrollRequest((n) => n + 1);
		}

		if (isError) {
			if (error.message.includes('Unauthorized')) {
				setErrorMessage('Authentication failed, please log out and back in');
				return;
			}

			// keep the page that's already shown; only the initial search's error explains the cause
			setErrorMessage(shownPage > 0 ? pageLoadFailureMessage(page) : error.message);
			return;
		}

		setErrorMessage(null);

		if (data) {
			setSearchResults(data.items);
			setTotal(data.total);
			setTotalPages(data.totalPages);
			setNoResultsMessage(data.items.length === 0 ? data.message ?? null : null);
			setLimitReached(!!data.limitReached);
			setShownPage(data.page);
		} else {
			setSearchResults([]);
			setTotal(0);
			setTotalPages(0);
			setNoResultsMessage(null);
			setLimitReached(false);
			setShownPage(0);
		}
	}, [data, isError, error]);

	// runs after the new results are in the dom; scrolling any earlier gets undone by
	// scroll anchoring when the old results are swapped out
	useEffect(() => {
		if (scrollRequest) {
			window.scrollTo({ top: 0 });
		}
	}, [scrollRequest]);

	useEffect(() => {
		document.documentElement.classList.toggle('fetching', isFetching);
		return () => document.documentElement.classList.remove('fetching');
	}, [isFetching]);

	useEffect(() => {
		if (!isFetching) {
			return;
		}
		const timer = setTimeout(() => setSlowSearch(true), SLOW_SEARCH_MS);
		return () => {
			clearTimeout(timer);
			setSlowSearch(false);
		};
	}, [isFetching]);

	useEffect(() => {
		if (!search) {
			return;
		}

		void refetch();
	}, [page, pageSize]);

	const startNewSearch = () => {
		setSearchResults([]);
		setTotal(0);
		setTotalPages(0);
		setNoResultsMessage(null);
		setLimitReached(false);
		setShownPage(0);

		if (page !== 1) {
			setPage(1);
			return;
		}

		void refetch();
	};

	const goToPage = (target: number) => {
		scrollToTopOnSettle.current = true;
		// after a failed page `page` is already the target, so setting it again wouldn't refetch
		if (target === page) {
			void refetch();
			return;
		}
		setPage(target);
	};

	return (
		<>
			<Nav />
			<main>
				<div className="search-filters-top">
					<label className="requires-manual-translations-only">
						<input
							type="checkbox"
							checked={requiresManualTranslationsOnly}
							onChange={(e) => {
								setRequiresManualTranslationsOnly(e.target.checked);
							}}
						/>
						Requires Manual Translations
					</label>

					<UsedInAreaFilters
						requestHistory={requestHistory}
						onRequestHistoryChange={setRequestHistory}
						translationType={translationType}
						onTranslationTypeChange={setTranslationType}
					/>
				</div>

				<form
					className="search-toolbar"
					onSubmit={(e) => {
						e.preventDefault();
						startNewSearch();
					}}
				>
					<textarea
						value={search}
						placeholder="Search by full text, partial text, translation id or regex."
						onKeyDown={(e) => {
							if (e.key === 'Enter' && e.ctrlKey) startNewSearch();
						}}
						onChange={(e) => {
							setSearch(e.target.value);
						}}
					></textarea>
					<input
						className="search-locale"
						type="text"
						value={searchLocale}
						onKeyDown={(e) => {
							if (e.key === 'Enter' && e.ctrlKey) startNewSearch();
						}}
						onChange={(e) => {
							setSearchLocale(e.target.value);
						}}
					></input>
					<select
						value={String(pageSize)}
						onChange={(e) => {
							setPageSize(Number(e.target.value));
							setPage(1);
						}}
					>
						<option value="10">10 per page</option>
						<option value="20">20 per page</option>
						<option value="50">50 per page</option>
					</select>
					<button type="submit" disabled={isFetching}>
						{isFetching ? 'Fetching...' : 'Search'}
					</button>
					{isFetching && (
						<button
							type="button"
							className="cancel-fetch"
							onClick={() => queryClient.cancelQueries({ queryKey: ['translations'] })}
						>
							Cancel
						</button>
					)}
				</form>

				{isFetching && slowSearch && <p className="slow-search">{SLOW_SEARCH_MESSAGE}</p>}
				{errorMessage && !isFetching && <p className="error">{errorMessage}</p>}
				{search && !isFetching && total > 0 && (
					<p>
						Showing page {shownPage} of {totalPages} ({formatTotal(total)} total results)
					</p>
				)}
				{search && !isFetching && searchResults.length === 0 && noResultsMessage && (
					<p className="no-results">
						{noResultsMessage}
						{limitReached && (
							<>
								{' '}
								<button
									type="button"
									onClick={() => {
										setTranslationType('');
										setPage(1);
										void refetch();
									}}
								>
									Search without Area filter
								</button>
							</>
						)}
					</p>
				)}

				{searchResults.map(
					(
						{
							translationId,
							translations,
							overrides,
							requestHistory,
							ColourCode,
							SizeCode,
							AttributeID,
							TranslationType,
							Title,
							CategoryCode,
							CategoryTranslationType,
							NoProductMatch,
							NoCategoryMatch,
							requiresManualTranslation,
						}
					) => {
						const localeEntries = Object.entries(translations);
						const primaryLocaleEntry =
							localeEntries.find(([locale]) => locale === 'en-GB') ??
							localeEntries[0];
						const selectedLocaleEntry = localeEntries.find(
							([locale]) => locale === searchLocale && locale !== primaryLocaleEntry?.[0]
						);
						const otherLocaleEntries = localeEntries.filter(
							([locale]) => locale !== primaryLocaleEntry?.[0] && locale !== selectedLocaleEntry?.[0]
						);

						if (!primaryLocaleEntry) {
							return null;
						}

						const [primaryLocale, primaryText] = primaryLocaleEntry;
						const isProductDomain = requestHistory.includes('ProductDomain');
						const isCategoryMetaDomain = requestHistory.includes(
							'CategoryMetaDomain'
						);
						const productFields = [
							isProductDomain && TranslationType
								? ['ProductArea', TranslationType]
								: null,
							isProductDomain && ColourCode ? ['ColourCode', ColourCode] : null,
							isProductDomain && Title ? ['Name', Title] : null,
							isProductDomain && SizeCode ? ['SizeCode', SizeCode] : null,
							isProductDomain && AttributeID ? ['AttributeID', AttributeID] : null,
							isProductDomain && NoProductMatch ? NO_MATCH_FIELD : null,
						].filter((field): field is [string, string] => field !== null);
						const categoryFields = [
							isCategoryMetaDomain && CategoryCode
								? ['CategoryCode', CategoryCode]
								: null,
							isCategoryMetaDomain && CategoryTranslationType
								? ['Category Area', CategoryTranslationType]
								: null,
							isCategoryMetaDomain && NoCategoryMatch
								? NO_MATCH_FIELD
								: null,
						].filter((field): field is [string, string] => field !== null);
						const hasProductIdentifier = Boolean(
							TranslationType || ColourCode || SizeCode || AttributeID || NoProductMatch
						);
						const hasCategoryIdentifier = Boolean(
							CategoryCode || CategoryTranslationType || NoCategoryMatch
						);
						const hasBothDomains = isProductDomain && isCategoryMetaDomain;
						const usedInDomains = [
							isProductDomain && (!hasBothDomains || hasProductIdentifier)
								? { name: 'Product', fields: productFields }
								: null,
							isCategoryMetaDomain && (!hasBothDomains || hasCategoryIdentifier)
								? { name: 'Category', fields: categoryFields }
								: null,
						].filter(
							(domain): domain is { name: string; fields: [string, string][] } =>
								domain !== null
						);

						return (
							<section key={translationId}>
								<h2 className="used-in">
									{usedInDomains.map(({ name, fields }) => (
										<span className="used-in-row" key={name}>
											<span className="used-in-label">Used in:</span>{' '}
											<span className="used-in-value">{name}</span>
											{fields.map(([label, value]) => (
												<span className="used-in-field" key={label}>
													<span className="used-in-label">{label}:</span>{' '}
													<span className="used-in-value">{value}</span>
												</span>
											))}
										</span>
									))}
								</h2>
								<TranslationLine
									refetch={refetch}
									key={translationId + primaryLocale}
									locale={primaryLocale}
									translation={primaryText}
									override={overrides?.[primaryLocale]?.text}
									translationId={translationId}
									isOverridable={primaryLocale !== 'en-GB'}
									canSaveOriginalAsOverride={false}
									requiresManualTranslation={false}
								/>
								{selectedLocaleEntry && (
									<TranslationLine
										refetch={refetch}
										key={translationId + selectedLocaleEntry[0]}
										locale={selectedLocaleEntry[0]}
										translation={selectedLocaleEntry[1]}
										override={overrides?.[selectedLocaleEntry[0]]?.text}
										translationId={translationId}
										isOverridable={selectedLocaleEntry[0] !== 'en-GB'}
										canSaveOriginalAsOverride={false}
										requiresManualTranslation={requiresManualTranslation?.includes(
											selectedLocaleEntry[0]
										)}
									/>
								)}

								{otherLocaleEntries.length > 0 && (
									<details>
										<summary>
											Other locales ({otherLocaleEntries.length})
										</summary>
										{otherLocaleEntries.map(([locale, text]) => (
											<TranslationLine
												refetch={refetch}
												key={translationId + locale}
												locale={locale}
												translation={text}
												override={overrides?.[locale]?.text}
												translationId={translationId}
												isOverridable={locale !== 'en-GB'}
												canSaveOriginalAsOverride={false}
												requiresManualTranslation={requiresManualTranslation?.includes(
													locale
												)}
											/>
										))}
									</details>
								)}
							</section>
						);
					}
				)}
				{searchResults.length !== 0 && search && (
					<>
						<button className="page-action" onClick={() => downloadCSV(search, searchResults)}>
							Download CSV
						</button>
						<div className="pagination-actions">
							<button
								onClick={() => {
									if (shownPage > 1) {
										goToPage(shownPage - 1);
									}
								}}
								disabled={isFetching || shownPage <= 1}
							>
								Previous page
							</button>
							<button
								onClick={() => {
									if (shownPage < totalPages) {
										goToPage(shownPage + 1);
									}
								}}
								disabled={isFetching || shownPage >= totalPages}
							>
								Next page
							</button>
						</div>
					</>
				)}
			</main>
		</>
	);
};

async function fetchTranslations({
	search,
	locale,
	page,
	pageSize,
	requiresManualTranslationsOnly,
	requestHistory,
	translationType,
	authFetch,
	signal,
}: {
	search: string;
	locale: string;
	page: number;
	pageSize: number;
	requiresManualTranslationsOnly: boolean;
	requestHistory: RequestHistoryDomain | null;
	translationType: string | null;
	authFetch: AuthFetch;
	signal?: AbortSignal;
}): Promise<EnrichedOverridesSearchResponse> {
	const params = new URLSearchParams({
		search,
		locale,
		page: String(page),
		pageSize: String(pageSize),
		requiresManualTranslationsOnly: String(requiresManualTranslationsOnly),
	});
	if (requestHistory) {
		params.set('requestHistory', requestHistory);
	}
	if (translationType) {
		params.set('translationType', translationType);
	}

	// aborts on cancel or once the search has run too long
	const controller = new AbortController();
	const abort = () => controller.abort();
	signal?.addEventListener('abort', abort);
	let timedOut = false;
	const timer = setTimeout(() => {
		timedOut = true;
		controller.abort();
	}, SEARCH_TIMEOUT_MS);

	let response: Response;
	try {
		response = await authFetch(
			`/api/overrides/enriched-search?${params.toString()}`,
			{
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
				},
				signal: controller.signal,
			}
		);
	} catch (error) {
		throw timedOut ? new Error(SEARCH_TOO_BROAD_MESSAGE) : error;
	} finally {
		clearTimeout(timer);
		signal?.removeEventListener('abort', abort);
	}

	if (!response.ok) {
		const responseBody: unknown = await response.json().catch(() => undefined);
		throw new Error(searchFailureMessage(response.status, response.statusText, responseBody));
	}
	const payload = (await response.json()) as EnrichedOverridesSearchResponse;
	assertValidUpdatedAtStrings(payload);
	return payload;
}

function escapeCSV(value: string): string {
	const escaped = value.replace(/"/g, '""');
	return `"${escaped}"`;
}

function downloadCSV(
	searchString: string,
	translations: EnrichedTranslationsModel[]
) {
	const locales = new Set<string>();
	const searchType = 'Manual Search';
	translations.forEach(({ translations }) => {
		Object.keys(translations).forEach((locale) => {
			locales.add(locale);
		});
	});

	let csvContent = 'text,resultType,id,' + Array.from(locales).join(',') + '\n';

	translations.forEach(({ translationId, translations, overrides }) => {
		csvContent += `${escapeCSV(searchString)},${escapeCSV(searchType)},${escapeCSV(translationId)},`;
		const row = Array.from(locales).map((locale) => {
			const activeTranslation =
				overrides && overrides[locale]
					? overrides[locale].text
					: translations[locale] ?? '';
			return escapeCSV(activeTranslation);
		});
		csvContent += row.join(',') + '\n';
	});

	const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
	const url = URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.setAttribute('href', url);
	link.setAttribute('download', 'overrides.csv');
	link.click();
	URL.revokeObjectURL(url);
}
